import { ReactNode, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCusAuth } from "@utils/store/auth/useCusAuth";
import { useKiosk } from "@screen/Kiosk/useKiosk";
import { fetchCustomerContexts } from "@screen/Onboarding/registerApi";

/**
 * 「等待安排瓦斯行」訂購鎖定閘門（2026-07-15 更正版）：
 * 等待名單客戶（註冊完成、沒配對到任何瓦斯行、無 CIS）可以進首頁、可以完成加入設定，
 * 但「瓦斯訂購」在平台對接完成前不開放 —— 此閘門只包訂購入口（gasShop / kiosk），
 * 命中時原頁面灰化、蓋上鎖定遮罩說明（設計見 waiting-arrangement mockup C）。
 *
 * 判定來源：GET /customer/contexts 的 waiting_supplier_arrangement（後端算：有等待列且無任何 CIS，
 * 平台對接建 CIS 後自動解除）。
 * 放行例外：
 *  - 本次 session 已有目標瓦斯行（entrySupplierId，QR/傳單掃碼進來正在自行綁定）→ 放行，
 *    讓客戶走完 完成設定 → 建 CIS 自行解套。
 *  - 未登入 / 判定失敗 → 放行（fail-open，不因 API 異常鎖死訂購）。未登入時不算檢查過，JWT 到了會補查（2026-10-15）。
 */
/** 判定結果的短期快取（60 秒）：切頁不重打 /customer/contexts。狀態改變（完成設定/對接）最多 60 秒內生效。 */
let waitingCache: { value: boolean; phoneVerified: boolean; jwt: string; ts: number } | null = null;
const WAITING_CACHE_MS = 60_000;

/** 這個 JWT 還有沒有沒過期的快取。 */
const freshCache = (jwt: string | null) =>
  jwt && waitingCache && waitingCache.jwt === jwt && Date.now() - waitingCache.ts < WAITING_CACHE_MS ? waitingCache : null;

/** 清掉判定快取（目前只給測試用）。 */
export const resetWaitingArrangementCache = () => {
  waitingCache = null;
};

const WaitingArrangementGate = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate();
  const { cusJwtToken } = useCusAuth();
  const { entrySupplierId, setNeedCheckoutOtp } = useKiosk();
  const initialCache = freshCache(cusJwtToken);
  const [waiting, setWaiting] = useState<boolean>(initialCache?.value ?? false);
  /**
   * 「為哪一個 JWT 檢查過」——不能只記 true/false（2026-10-15 dev 實測 bug）：
   * 從 LINE 直接開 /kiosk、/gasShop 時，頁面先畫出來，JWT 晚 1～2 秒才到。舊版在「還沒登入」那一刻
   * 就標成檢查完，JWT 到了也不再檢查 → needCheckoutOtp 停在預設 false → 結算頁不出驗證卡。
   * /kiosk → /kiosk/confirm 又沿用同一個 Gate 實例（不重新掛載），所以一定要在這裡依 JWT 重查。
   */
  const [checkedJwt, setCheckedJwt] = useState<string | null>(initialCache ? cusJwtToken : null);

  useEffect(() => {
    if (!cusJwtToken) {
      // 未登入：放行（fail-open），但不算檢查過；JWT 一到，checkedJwt !== cusJwtToken 就會重查。
      setWaiting(false);
      return;
    }
    if (checkedJwt === cusJwtToken) {
      // 已為這個身分檢查過：不重打 API，只把「是否已驗過手機」回填 store。
      if (waitingCache && waitingCache.jwt === cusJwtToken) setNeedCheckoutOtp(!waitingCache.phoneVerified);
      return;
    }
    const hit = freshCache(cusJwtToken);
    if (hit) {
      // 快取命中（例如重新掛載）：不重打，回填 store。
      setWaiting(hit.value);
      setNeedCheckoutOtp(!hit.phoneVerified);
      setCheckedJwt(cusJwtToken);
      return;
    }
    const jwt = cusJwtToken;
    let cancelled = false; // 檢查途中 JWT 又換了 → 舊結果作廢
    (async () => {
      try {
        const res = await fetchCustomerContexts(jwt);
        if (cancelled) return;
        const w = res.data?.data?.waiting_supplier_arrangement === true;
        // 順道取回「是否已驗過手機」寫入 kiosk store（同一支 API，不另打一次）。
        // 只有後端明確說 false 才要求結帳補驗；欄位缺漏（舊版後端）視為已驗，不擋正常客戶。
        setNeedCheckoutOtp(res.data?.data?.phone_verified === false);
        waitingCache = { value: w, phoneVerified: res.data?.data?.phone_verified !== false, jwt, ts: Date.now() };
        setWaiting(w);
        if (w) {
          // eslint-disable-next-line no-console
          console.log("[等待安排] waiting_supplier_arrangement=true → 訂購鎖定（首頁/完成設定不受影響）");
        }
      } catch {
        if (cancelled) return;
        setWaiting(false); // fail-open：判定失敗不鎖訂購（不因 API 異常鎖死）
      } finally {
        if (!cancelled) setCheckedJwt(jwt);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [cusJwtToken, checkedJwt, setNeedCheckoutOtp]);

  // 三種狀態（放行／檢查中擋互動／鎖定）都把 children 放在「同一個固定位置」（同一個 div），
  // 只切 style。不可用不同樹結構分支回傳——children 位置一變 React 會整頁 remount（effect 重打風暴）。
  //
  // 佈局關鍵：放行/檢查中用 `display: contents` —— 完全不產生盒子，不打斷 kiosk/gasShop
  // 頁根元素的 height:100% 鏈與 positioned ancestor（kiosk 的底部 sheet 全是 absolute 貼頁根，
  // 中間多一層有大小的 div 會把 sheet 錨到「整頁內容高度」→ modal 跑版到視窗外，2026-07-16 實測）。
  // 檢查中擋互動靠 pointer-events（繼承屬性，display:contents 也會傳給子樹）。
  // 鎖定時 children 需要灰化（filter 需要真實盒子）才給一個 block；遮罩用 fixed 錨定視窗，不依賴祖先。
  const bypass = !!entrySupplierId;
  const checkedForCurrent = !!cusJwtToken && checkedJwt === cusJwtToken;
  const locked = !bypass && checkedForCurrent && waiting;
  // 檢查中（已登入、還沒為這個 JWT 檢查完）：擋互動避免可下單空窗。沒登入不擋（fail-open）。
  const guarding = !bypass && !!cusJwtToken && !checkedForCurrent;

  return (
    <>
      <div
        style={
          locked
            ? { height: "100%", width: "100%", opacity: 0.4, filter: "grayscale(1)", pointerEvents: "none", userSelect: "none" }
            : { display: "contents", pointerEvents: guarding ? "none" : undefined }
        }
        aria-hidden={locked || undefined}
        aria-busy={guarding || undefined}
      >
        {children}
      </div>
      {locked && (
      <div className="fixed inset-0 z-[1000005] flex flex-col items-center justify-center gap-2.5 px-8 text-center" style={{ background: "rgba(255,255,255,0.78)", backdropFilter: "blur(2px)" }}>
        <style>{`
          @keyframes wagBreathe { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.08); } }
          @keyframes wagDot { 0%, 100% { opacity: .25; transform: translateY(0); } 50% { opacity: 1; transform: translateY(-2px); } }
        `}</style>
        <div className="w-[52px] h-[52px] rounded-[16px] flex items-center justify-center mb-1" style={{ background: "linear-gradient(145deg,#FBBF24,#F59E0B)", animation: "wagBreathe 2.6s ease-in-out infinite", boxShadow: "0 8px 22px -8px rgba(245,158,11,.6)" }}>
          <svg width="27" height="27" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="8.5" stroke="#fff" strokeWidth="2" />
            {/* 指針緩慢旋轉 → 傳達「等待中、時間在走」 */}
            <path d="M12 7.5V12l3 2" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <animateTransform attributeName="transform" attributeType="XML" type="rotate" from="0 12 12" to="360 12 12" dur="6s" repeatCount="indefinite" />
            </path>
          </svg>
        </div>
        <div className="text-[16.5px] font-black text-[#0f1a35]">訂購功能即將開放</div>
        <div className="text-[12.5px] text-[#64748B] leading-[1.7]">
          我們正在為您安排附近的合作瓦斯行，
          <br />
          安排好就能直接叫瓦斯，並用 LINE 通知您。
        </div>
        <div className="flex items-center gap-1.5 bg-[#FFF8E8] border border-[#F2DFAE] text-[#7A4E09] text-[12px] font-extrabold rounded-full px-3.5 py-1.5 mt-1">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="8.5" stroke="#7A4E09" strokeWidth="2" />
            <path d="M12 7.5V12l3 2" stroke="#7A4E09" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          安排瓦斯行中
          <span className="inline-flex items-end gap-[3px] ml-0.5 mb-[2px]">
            {[0, 1, 2].map((i) => (
              <span key={i} className="w-[3px] h-[3px] rounded-full bg-[#7A4E09]" style={{ animation: `wagDot 1.4s ${i * 0.18}s ease-in-out infinite` }} />
            ))}
          </span>
        </div>
        <button
          onClick={() => navigate("/home")}
          className="mt-4 bg-[#2196f3] text-white text-[14px] font-extrabold rounded-[13px] border-none px-9 py-3 cursor-pointer"
        >
          回首頁
        </button>
      </div>
      )}
    </>
  );
};

export default WaitingArrangementGate;
